// src/components/Maintain.jsx
// verticalstack — S36 Layer 2 Maintain absorption
//
// Absorbs 4 PMApp routes:
//   /work-orders      → Work Orders tab (table view, nested clients/{cid}/workOrders)
//   /work-order-board → Board tab (kanban, same data, client-side groupBy)
//   /network          → merged into Vendors tab (Directory section)
//   /vendors          → Vendors tab (Active Subs section + Directory section)
//
// Plus duplications per AS-PP / AS-QQ:
//   Messages tab (vendor/sub-filtered)
//   Assets tab (Create-WO emphasis)
//
// 6 top tabs: Overview | Work Orders | Board | Vendors | Messages | Assets
//
// Path B "one absorption = one component" pattern (AS-KK).
// Read-only Layer 2 (AS-DD); writes are toast stubs (AS-Q) → Layer 2.5 CFs.
// Inline hex literals (AS-X) — no brand.js import.
//
// New findings logged in S36 handoff:
//   AS-RR: workOrders nested at clients/{cid}/workOrders (parallel to AS-NN)
//   AS-SS: WorkOrderBoard + VendorRoster top-level WO query broken — Maintain fixes by reusing nested path
//   AS-TT: RESOLVED mid-session — Network and Vendors merged into single Vendors tab with internal
//          kind filter (Active Subs / Directory / All). Two collections preserved at data layer
//          (clientMembers role=Subcontractor + clients/{cid}/vendors); rendered side-by-side
//          with kind badges and a segmented kind filter. Trustack-vs-engagement distinction
//          communicated via per-row badge, not via separate tab.
//   AS-UU: 8-state WO status enum reconciliation (new|open|assigned|in_progress|completed|pending_payment|paid|cancelled)
//   AS-VV: stray eslint-disable directive in useNestedClientCollection rejected by CRA build — removed; deps were already correct
//   AS-WW: tab strip overflow scrolling not always reachable in tight viewports (7→6 tab merge mitigates; further fix in cleanup pass)
//
// S6.B (May 19, 2026) — CLOSES AS-S4-007:
//   messageThreads read RETARGETED from nested clients/{cid}/messageThreads
//   (deleted in S2, no rule coverage) to flat top-level /messages collection
//   per Path Z architecture (AS-S6-001). Client-side filter retained:
//   MAINTAIN_MESSAGE_ROLES applied to derived participantRole via phone
//   lookup. Status derivation uses S6 close handoff §6c rules. See
//   AS-S6-005 in S6 matrix for full closure rationale.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';

// ───────────────────────────────────────────────────────────────────────────
// CONFIG — tabs, status maps, filters
// ───────────────────────────────────────────────────────────────────────────

const MAINTAIN_TABS = [
  { key: 'overview',  label: 'Overview' },
  { key: 'wos',       label: 'Work Orders' },
  { key: 'board',     label: 'Board' },
  { key: 'vendors',   label: 'Vendors' },
  { key: 'messages',  label: 'Messages' },
  { key: 'assets',    label: 'Assets' },
];

// AS-UU: reconciled 8-state WO status enum from List + Board + Detail
const WO_STATUS = {
  new:             { label: 'New',             bg: '#E0F2FE', color: '#075985' },
  open:            { label: 'Open',            bg: '#E0F2FE', color: '#075985' },
  assigned:        { label: 'Assigned',        bg: '#FEF3C7', color: '#78350F' },
  in_progress:     { label: 'In Progress',     bg: '#FEF3C7', color: '#92400E' },
  completed:       { label: 'Completed',       bg: '#DCFCE7', color: '#15803D' },
  pending_payment: { label: 'Pending Payment', bg: '#FED7AA', color: '#9A3412' },
  paid:            { label: 'Paid',            bg: '#F1F5F9', color: '#475569' },
  cancelled:       { label: 'Cancelled',       bg: '#FEE2E2', color: '#991B1B' },
};

const URGENCY_CONFIG = {
  low:       { label: 'Low',       color: '#16A34A', bg: '#F0FDF4' },
  medium:    { label: 'Medium',    color: '#D97706', bg: '#FFFBEB' },
  high:      { label: 'High',      color: '#DC2626', bg: '#FEF2F2' },
  emergency: { label: 'Emergency', color: '#7C3AED', bg: '#F5F3FF' },
};

// Board columns — 5 active flow states (cancelled filtered out)
const BOARD_COLUMNS = [
  { key: 'open',            label: 'Open',            color: '#0891B2', bg: '#E0F2FE' },
  { key: 'assigned',        label: 'Assigned',        color: '#A16207', bg: '#FEF3C7' },
  { key: 'in_progress',     label: 'In Progress',     color: '#D97706', bg: '#FFFBEB' },
  { key: 'completed',       label: 'Completed',       color: '#16A34A', bg: '#F0FDF4' },
  { key: 'pending_payment', label: 'Pending Payment', color: '#EA580C', bg: '#FFF7ED' },
];

const WO_FILTER_TABS = [
  { key: 'all',             label: 'All' },
  { key: 'open',            label: 'Open' },
  { key: 'in_progress',     label: 'In Progress' },
  { key: 'completed',       label: 'Completed' },
  { key: 'pending_payment', label: 'Pending Payment' },
  { key: 'paid',            label: 'Paid' },
];

// AS-PP: filter Maintain Messages to vendor/sub/contractor threads
// S6.B (May 19, 2026): role list unchanged; derivation now applied to
// /messages records (not /messageThreads). Role match is attempted in
// two ways for forward + back-compat:
//   1. msg.participantRole (set explicitly when /messageThreads docs
//      exist post-S11/S12 and are joined — not used at S6 yet)
//   2. Implicit derivation: msg.workOrderId presence → assumed vendor
//      thread (covers most Maintain-side traffic per AS-PP scope).
// Future enhancement: hydrate users by phone, read users.role.
const MAINTAIN_MESSAGE_ROLES = ['vendor', 'sub', 'contractor', 'subcontractor', 'Subcontractor'];

// Asset condition tiers (matches S11 spec via S35 AssetsView)
const CONDITION_CONFIG = {
  excellent: { label: 'Excellent', color: '#15803D', bg: '#DCFCE7', min: 9 },
  good:      { label: 'Good',      color: '#16A34A', bg: '#F0FDF4', min: 7 },
  fair:      { label: 'Fair',      color: '#D97706', bg: '#FFFBEB', min: 5 },
  poor:      { label: 'Poor',      color: '#DC2626', bg: '#FEF2F2', min: 3 },
  critical:  { label: 'Critical',  color: '#991B1B', bg: '#FEE2E2', min: 1 },
};

const TRADE_OPTIONS = [
  'General', 'Plumbing', 'HVAC', 'Electrical', 'Roofing', 'Flooring',
  'Painting', 'Landscaping', 'Cleaning', 'Pest Control', 'Appliance Repair',
  'Carpentry', 'Masonry', 'Pool Service', 'Other',
];

// ───────────────────────────────────────────────────────────────────────────
// HELPERS
// ───────────────────────────────────────────────────────────────────────────

const db = getFirestore();

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateLong(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtMoneyCents(cents) {
  if (cents == null || isNaN(cents)) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toLocaleString()}`;
}

function parseAssetDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function getAssetCondition(score) {
  if (score == null || isNaN(score)) return null;
  if (score >= 9) return CONDITION_CONFIG.excellent;
  if (score >= 7) return CONDITION_CONFIG.good;
  if (score >= 5) return CONDITION_CONFIG.fair;
  if (score >= 3) return CONDITION_CONFIG.poor;
  return CONDITION_CONFIG.critical;
}

function getAssignedName(wo) {
  // Reconciles assignedSubName (List) vs assignedToName (Board) inconsistency
  return wo.assignedSubName || wo.assignedToName || null;
}

// ───────────────────────────────────────────────────────────────────────────
// HOOKS — read paths
// ───────────────────────────────────────────────────────────────────────────

// AS-RR: New hook for nested clients/{clientId}/{subPath} reads.
// Mirrors useWorkspaceCollection shape from S34. Used for workOrders, vendors,
// and (eventually post-AS-NN-migration) messageThreads.
function useNestedClientCollection(clientId, subPath, opts = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId || !subPath) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    let q;
    try {
      const colRef = collection(db, 'clients', clientId, subPath);
      q = opts.orderField
        ? query(colRef, orderBy(opts.orderField, opts.orderDir || 'desc'))
        : query(colRef);
    } catch (err) {
      console.warn(`[Maintain] ${subPath} query build failed:`, err);
      setError(err);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        // AS-W fallback retry: try without orderBy if missing index
        if (opts.orderField) {
          console.warn(`[Maintain] ${subPath} orderBy retry — trying unordered`);
          try {
            const colRef = collection(db, 'clients', clientId, subPath);
            const fallback = onSnapshot(
              query(colRef),
              (snap) => {
                setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
                setLoading(false);
              },
              (err2) => {
                console.warn(`[Maintain] ${subPath} fallback failed:`, err2);
                setError(err2);
                setLoading(false);
              }
            );
            return () => fallback();
          } catch (err2) {
            setError(err2);
            setLoading(false);
          }
        } else {
          console.warn(`[Maintain] ${subPath} read failed:`, err);
          setError(err);
          setLoading(false);
        }
      }
    );

    return () => unsub();
  }, [clientId, subPath, opts.orderField, opts.orderDir]);

  return { data, loading, error };
}

// useWorkspaceCollection variant: top-level collection with workspaceId/clientId filter.
// Matches S34 hook shape. Used for clientMembers (Vendors tab) and assets (Assets tab).
// S6.B (May 19, 2026): also used for /messages read (Messages tab) per Path Z.
function useFilteredCollection(collectionName, filterField, filterValue, extraFilter) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!filterValue) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    let q;
    try {
      const colRef = collection(db, collectionName);
      const constraints = [where(filterField, '==', filterValue)];
      if (extraFilter) {
        constraints.push(where(extraFilter.field, extraFilter.op, extraFilter.value));
      }
      q = query(colRef, ...constraints);
    } catch (err) {
      console.warn(`[Maintain] ${collectionName} query build failed:`, err);
      setError(err);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.warn(`[Maintain] ${collectionName} read failed:`, err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [collectionName, filterField, filterValue, extraFilter]);

  return { data, loading, error };
}

// ───────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ───────────────────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const meta = WO_STATUS[status] || { label: status || 'Unknown', bg: '#F1F5F9', color: '#475569' };
  return (
    <span style={{ ...S.pill, background: meta.bg, color: meta.color }}>
      {meta.label}
    </span>
  );
}

function UrgencyBadge({ urgency }) {
  if (!urgency) return null;
  const meta = URGENCY_CONFIG[urgency.toLowerCase()] || URGENCY_CONFIG.low;
  return (
    <span style={{ ...S.urgencyBadge, color: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
}

function Toast({ message, onDismiss }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDismiss, 2500);
    return () => clearTimeout(t);
  }, [message, onDismiss]);
  if (!message) return null;
  return <div style={S.toast}>{message}</div>;
}

function EmptyState({ title, sub }) {
  return (
    <div style={S.empty}>
      <div style={S.emptyTitle}>{title}</div>
      {sub && <div style={S.emptySub}>{sub}</div>}
    </div>
  );
}

function LoadingState({ label }) {
  return <div style={S.loading}>{label || 'Loading…'}</div>;
}

function ErrorState({ label, hint }) {
  return (
    <div style={S.error}>
      <div style={S.errorTitle}>{label || 'Could not load'}</div>
      {hint && <div style={S.errorHint}>{hint}</div>}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TAB: OVERVIEW
// ───────────────────────────────────────────────────────────────────────────

function OverviewView({ wos, vendors, subs, assets, messages, onToast }) {
  const totalWos = wos.length;
  const openCount = wos.filter((w) =>
    ['new', 'open', 'assigned', 'in_progress'].includes(w.status)
  ).length;
  const pendingPayment = wos.filter((w) => w.status === 'pending_payment').length;
  const emergencyCount = wos.filter(
    (w) => w.urgency === 'emergency' && w.status !== 'paid' && w.status !== 'cancelled'
  ).length;

  const activeSubsCount = subs.filter((s) => s.status === 'active').length;
  const networkCount = vendors.length;

  const lowConditionAssets = assets.filter((a) => {
    const cond = getAssetCondition(a.conditionScore);
    return cond && (cond === CONDITION_CONFIG.poor || cond === CONDITION_CONFIG.critical);
  }).length;

  return (
    <div style={S.tabBody}>
      <div style={S.sectionHead}>
        <div>
          <div style={S.sectionTitle}>Maintainstack overview</div>
          <div style={S.sectionSub}>
            {totalWos} work orders · {networkCount} vendors in network · {activeSubsCount} active subs
          </div>
        </div>
        <button
          style={S.btnPrimary}
          onClick={() => onToast('AI maintenance assist — Coming in Layer 2.5')}
        >
          ✨ AI Assist
        </button>
      </div>

      <div style={S.kpiGrid}>
        <div style={S.kpiTile}>
          <div style={S.kpiLabel}>Open WOs</div>
          <div style={S.kpiValue}>{openCount}</div>
          <div style={S.kpiSub}>{emergencyCount} emergency</div>
        </div>
        <div style={S.kpiTile}>
          <div style={S.kpiLabel}>Pending Payment</div>
          <div style={S.kpiValue}>{pendingPayment}</div>
          <div style={S.kpiSub}>awaiting release</div>
        </div>
        <div style={S.kpiTile}>
          <div style={S.kpiLabel}>Active Subs</div>
          <div style={S.kpiValue}>{activeSubsCount}</div>
          <div style={S.kpiSub}>of {subs.length} total</div>
        </div>
        <div style={S.kpiTile}>
          <div style={S.kpiLabel}>Low-condition Assets</div>
          <div style={S.kpiValue}>{lowConditionAssets}</div>
          <div style={S.kpiSub}>poor or critical</div>
        </div>
      </div>

      <div style={S.overviewSplit}>
        <div style={S.overviewCard}>
          <div style={S.overviewCardHead}>Recent work orders</div>
          {wos.length === 0 ? (
            <div style={S.overviewEmpty}>No work orders yet.</div>
          ) : (
            wos.slice(0, 5).map((wo) => (
              <div key={wo.id} style={S.overviewRow}>
                <div style={S.overviewRowLeft}>
                  <div style={S.overviewRowTitle}>
                    {wo.title || wo.summary || wo.description || '(no title)'}
                  </div>
                  <div style={S.overviewRowSub}>
                    {wo.propertyName || wo.propertyAddress || '—'} · {fmtDate(wo.createdAt)}
                  </div>
                </div>
                <StatusPill status={wo.status} />
              </div>
            ))
          )}
        </div>

        <div style={S.overviewCard}>
          <div style={S.overviewCardHead}>Vendors at a glance</div>
          <div style={S.overviewRow}>
            <div style={S.overviewRowLeft}>
              <div style={S.overviewRowTitle}>Active subcontractors</div>
              <div style={S.overviewRowSub}>Have logins · workspace engagements</div>
            </div>
            <div style={S.overviewRowCount}>{activeSubsCount}</div>
          </div>
          <div style={S.overviewRow}>
            <div style={S.overviewRowLeft}>
              <div style={S.overviewRowTitle}>Directory pool</div>
              <div style={S.overviewRowSub}>External vendors · Trustack-rated</div>
            </div>
            <div style={S.overviewRowCount}>{networkCount}</div>
          </div>
          <div style={S.overviewRow}>
            <div style={S.overviewRowLeft}>
              <div style={S.overviewRowTitle}>Vendor messages</div>
              <div style={S.overviewRowSub}>Active threads with vendors/subs</div>
            </div>
            <div style={S.overviewRowCount}>{messages.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TAB: WORK ORDERS
// ───────────────────────────────────────────────────────────────────────────

function WorkOrdersView({ wos, loading, error, onToast }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return wos.filter((wo) => {
      if (filter !== 'all' && wo.status !== filter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (wo.propertyName || '').toLowerCase().includes(s) ||
          (wo.description || '').toLowerCase().includes(s) ||
          (wo.title || '').toLowerCase().includes(s) ||
          (getAssignedName(wo) || '').toLowerCase().includes(s) ||
          (wo.id || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [wos, filter, search]);

  const counts = useMemo(() => {
    return WO_FILTER_TABS.reduce((acc, f) => {
      acc[f.key] = f.key === 'all' ? wos.length : wos.filter((w) => w.status === f.key).length;
      return acc;
    }, {});
  }, [wos]);

  if (loading) return <LoadingState label="Loading work orders…" />;
  if (error) {
    return (
      <ErrorState
        label="Could not load work orders"
        hint="Workspace permissions may be loading. Refresh in a moment."
      />
    );
  }

  return (
    <div style={S.tabBody}>
      <div style={S.sectionHead}>
        <div>
          <div style={S.sectionTitle}>Work Orders</div>
          <div style={S.sectionSub}>
            {wos.length} total · {counts.open || 0} open
            {counts.pending_payment > 0 && ` · ${counts.pending_payment} pending payment`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={S.btnGhost}
            onClick={() => onToast('AI triage — Coming in Layer 2.5')}
          >
            ✨ AI Assist
          </button>
          <button
            style={S.btnPrimary}
            onClick={() => onToast('Create work order — Coming in Layer 2.5')}
          >
            + New WO
          </button>
        </div>
      </div>

      <div style={S.toolbar}>
        <div style={S.filterTabs}>
          {WO_FILTER_TABS.map((f) => (
            <button
              key={f.key}
              style={{
                ...S.filterTab,
                ...(filter === f.key ? S.filterTabActive : {}),
              }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {counts[f.key] > 0 && (
                <span
                  style={{
                    ...S.filterTabCount,
                    ...(filter === f.key ? S.filterTabCountActive : {}),
                  }}
                >
                  {counts[f.key]}
                </span>
              )}
            </button>
          ))}
        </div>
        <input
          style={S.search}
          placeholder="Search WOs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={search || filter !== 'all' ? 'No matches' : 'No work orders yet'}
          sub={
            search || filter !== 'all'
              ? 'Try adjusting filter or search.'
              : 'Create one to get started.'
          }
        />
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['WO #', 'Property', 'Description', 'Urgency', 'Assigned', 'Status', 'Amount', 'Created'].map(
                  (h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((wo) => {
                const assigned = getAssignedName(wo);
                const amount =
                  wo.paidAmountCents != null
                    ? fmtMoneyCents(wo.paidAmountCents)
                    : wo.quotedAmountCents != null
                    ? fmtMoneyCents(wo.quotedAmountCents)
                    : '—';
                return (
                  <tr
                    key={wo.id}
                    style={S.tableRow}
                    onClick={() => onToast(`Open WO ${wo.id.slice(0, 6).toUpperCase()} — Coming in Layer 2.5`)}
                  >
                    <td style={S.td}>
                      <span style={S.woId}>#{(wo.id || '').slice(0, 6).toUpperCase()}</span>
                    </td>
                    <td style={S.td}>{wo.propertyName || wo.propertyAddress || '—'}</td>
                    <td style={{ ...S.td, ...S.tdDesc }} title={wo.description || ''}>
                      {wo.title || wo.summary || wo.description || '—'}
                    </td>
                    <td style={S.td}>
                      <UrgencyBadge urgency={wo.urgency} />
                    </td>
                    <td style={S.td}>
                      {assigned || <span style={S.unassigned}>Unassigned</span>}
                    </td>
                    <td style={S.td}>
                      <StatusPill status={wo.status} />
                    </td>
                    <td style={S.td}>{amount}</td>
                    <td style={S.td}>{fmtDate(wo.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TAB: BOARD (kanban — same data as Work Orders, client-side groupBy)
// ───────────────────────────────────────────────────────────────────────────

function BoardView({ wos, loading, error, onToast }) {
  const [search, setSearch] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('all');

  const filtered = useMemo(() => {
    return wos.filter((wo) => {
      if (urgencyFilter !== 'all' && (wo.urgency || '').toLowerCase() !== urgencyFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (wo.propertyName || '').toLowerCase().includes(s) ||
          (wo.description || '').toLowerCase().includes(s) ||
          (wo.title || '').toLowerCase().includes(s) ||
          (getAssignedName(wo) || '').toLowerCase().includes(s) ||
          (wo.id || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [wos, search, urgencyFilter]);

  const totalOpen = wos.filter((w) =>
    ['new', 'open', 'assigned', 'in_progress'].includes(w.status)
  ).length;

  if (loading) return <LoadingState label="Loading board…" />;
  if (error) {
    return (
      <ErrorState
        label="Could not load board"
        hint="Same data as Work Orders tab. Refresh if it persists."
      />
    );
  }

  return (
    <div style={S.tabBody}>
      <div style={S.sectionHead}>
        <div>
          <div style={S.sectionTitle}>Work Order Board</div>
          <div style={S.sectionSub}>{totalOpen} active across {BOARD_COLUMNS.length} columns</div>
        </div>
      </div>

      <div style={S.toolbar}>
        <input
          style={{ ...S.search, flex: 1 }}
          placeholder="Search by property, description, vendor, or ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={S.select}
          value={urgencyFilter}
          onChange={(e) => setUrgencyFilter(e.target.value)}
        >
          <option value="all">All Urgencies</option>
          <option value="emergency">Emergency</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      <div style={S.board}>
        {BOARD_COLUMNS.map((col) => {
          const items = filtered.filter((wo) => wo.status === col.key);
          return (
            <div key={col.key} style={S.boardColumn}>
              <div style={{ ...S.boardColHeader, borderTop: `3px solid ${col.color}` }}>
                <span style={{ ...S.boardColTitle, color: col.color }}>{col.label}</span>
                <span style={{ ...S.boardColCount, background: col.bg, color: col.color }}>
                  {items.length}
                </span>
              </div>
              <div style={S.boardColBody}>
                {items.length === 0 ? (
                  <div style={S.boardEmpty}>No work orders</div>
                ) : (
                  items.map((wo) => {
                    const assigned = getAssignedName(wo);
                    return (
                      <div
                        key={wo.id}
                        style={S.boardCard}
                        onClick={() =>
                          onToast(`Open WO ${wo.id.slice(0, 6).toUpperCase()} — Coming in Layer 2.5`)
                        }
                        title="Open work order"
                      >
                        <div style={S.boardCardTop}>
                          <span style={S.boardCardId}>
                            #{(wo.id || '').slice(-6).toUpperCase()}
                          </span>
                          <UrgencyBadge urgency={wo.urgency} />
                        </div>
                        <div style={S.boardCardDesc}>
                          {wo.title || wo.summary || wo.description || '(no description)'}
                        </div>
                        <div style={S.boardCardMeta}>
                          <div style={S.boardCardMetaRow}>
                            🏠 {wo.propertyName || (wo.propertyId || '').slice(0, 8) || '—'}
                          </div>
                          {assigned && <div style={S.boardCardMetaRow}>👷 {assigned}</div>}
                        </div>
                        <div style={S.boardCardFooter}>
                          <span style={S.boardCardDate}>{fmtDate(wo.createdAt)}</span>
                          {wo.quotedAmountCents != null && (
                            <span style={S.boardCardAmount}>
                              ${(wo.quotedAmountCents / 100).toFixed(0)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TAB: VENDORS (combined — active subs + directory pool)
// AS-TT RESOLVED (S36 mid-session): Network and Vendors merged into a single
// Vendors tab with internal kind filter (Active subs / Directory / All).
// Two distinct collections preserved at data layer:
//   - clientMembers where role=Subcontractor   (auth + WO count + status)
//   - clients/{cid}/vendors                    (directory pool, Trustack ratings)
// Rendered side-by-side with a `kind` badge on each row.
// ───────────────────────────────────────────────────────────────────────────

function VendorsView({ subs, vendors, wos, subsLoading, vendorsLoading,
                      subsError, vendorsError, onToast }) {
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all'); // all | sub | directory
  const [tradeFilter, setTradeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active | suspended | all

  const loading = subsLoading || vendorsLoading;
  // Show error only if BOTH sources fail; partial success still renders the
  // working pool with a soft warning.
  const bothFailed = !!subsError && !!vendorsError;

  // Open WO count per sub — derived from already-loaded wos (no extra query)
  const openWOByUid = useMemo(() => {
    const out = {};
    wos.forEach((wo) => {
      if (!wo.assignedTo) return;
      if (!['open', 'assigned', 'in_progress', 'completed', 'pending_payment'].includes(wo.status)) return;
      out[wo.assignedTo] = (out[wo.assignedTo] || 0) + 1;
    });
    return out;
  }, [wos]);

  // Normalize both pools into a single shape for unified rendering
  const unified = useMemo(() => {
    const subRows = (subs || []).map((s) => ({
      kind: 'sub',
      id: s.id,
      uid: s.uid || s.userId || s.id,
      displayName: s.displayName || s.email || 'Unnamed Sub',
      email: s.email || null,
      phone: s.phone || null,
      trade: s.trade || null,
      status: s.status || 'active', // active | suspended
      openWOCount: openWOByUid[s.uid || s.userId] || 0,
      // sub-only fields
      hasAuth: true,
      // directory-only fields default empty
      category: null,
      rating: null,
      insuranceVerified: false,
      serviceArea: null,
      _raw: s,
    }));

    const dirRows = (vendors || []).map((v) => ({
      kind: 'directory',
      id: v.id,
      uid: null,
      displayName: v.name || 'Unnamed Vendor',
      email: v.contactEmail || null,
      phone: v.contactPhone || null,
      trade: v.category || null, // directory uses `category` as trade-equivalent
      status: 'active', // directory entries are not suspendable in v1
      openWOCount: null,
      hasAuth: false,
      category: v.category || null,
      rating: typeof v.rating === 'number' ? v.rating : null,
      insuranceVerified: !!v.insuranceVerified,
      serviceArea: v.serviceArea || null,
      _raw: v,
    }));

    return [...subRows, ...dirRows];
  }, [subs, vendors, openWOByUid]);

  // Trade dropdown sources from BOTH pools
  const trades = useMemo(() => {
    const uniq = new Set();
    unified.forEach((r) => { if (r.trade) uniq.add(r.trade); });
    return ['all', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [unified]);

  const filtered = useMemo(() => {
    return unified.filter((r) => {
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (tradeFilter !== 'all' && r.trade !== tradeFilter) return false;
      // status filter only applies to subs; directory entries always pass
      if (r.kind === 'sub' && statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const match =
          (r.displayName || '').toLowerCase().includes(s) ||
          (r.email || '').toLowerCase().includes(s) ||
          (r.trade || '').toLowerCase().includes(s) ||
          (r.serviceArea || '').toLowerCase().includes(s);
        if (!match) return false;
      }
      return true;
    });
  }, [unified, kindFilter, tradeFilter, statusFilter, search]);

  const subsCount = unified.filter((r) => r.kind === 'sub').length;
  const activeSubsCount = unified.filter((r) => r.kind === 'sub' && r.status === 'active').length;
  const dirCount = unified.filter((r) => r.kind === 'directory').length;
  const totalOpenWOs = unified.reduce((sum, r) => sum + (r.openWOCount || 0), 0);

  if (loading) return <LoadingState label="Loading vendors…" />;
  if (bothFailed) {
    return (
      <ErrorState
        label="Could not load vendors"
        hint="Both subs and directory queries failed. Workspace rules audit pending (AS-HH/JJ)."
      />
    );
  }

  return (
    <div style={S.tabBody}>
      <div style={S.sectionHead}>
        <div>
          <div style={S.sectionTitle}>Vendors</div>
          <div style={S.sectionSub}>
            {activeSubsCount} active sub{activeSubsCount !== 1 ? 's' : ''} ·
            {' '}{dirCount} in directory ·
            {' '}{totalOpenWOs} open WO{totalOpenWOs !== 1 ? 's' : ''} assigned
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={S.btnGhost}
            onClick={() => onToast('AI vendor match (AI-06) — Coming in Layer 2.5')}
          >
            ✨ Find Best Match
          </button>
          <button
            style={S.btnPrimary}
            onClick={() => onToast('Invite vendor — Coming in Layer 2.5')}
          >
            + Invite Vendor
          </button>
        </div>
      </div>

      {/* Kind filter — segmented control */}
      <div style={S.kindFilterRow}>
        {[
          { key: 'all',       label: `All (${unified.length})` },
          { key: 'sub',       label: `Active Subs (${subsCount})` },
          { key: 'directory', label: `Directory (${dirCount})` },
        ].map((f) => (
          <button
            key={f.key}
            style={{
              ...S.kindFilterBtn,
              ...(kindFilter === f.key ? S.kindFilterBtnActive : {}),
            }}
            onClick={() => setKindFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Soft warnings if one source failed but the other worked */}
      {subsError && !vendorsError && (
        <div style={S.softWarn}>
          Active subs query failed — showing directory only.
        </div>
      )}
      {vendorsError && !subsError && (
        <div style={S.softWarn}>
          Directory query failed — showing active subs only.
        </div>
      )}

      <div style={S.toolbar}>
        <input
          style={{ ...S.search, flex: 1 }}
          placeholder="Search by name, email, trade, or service area…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={S.select}
          value={tradeFilter}
          onChange={(e) => setTradeFilter(e.target.value)}
        >
          <option value="all">All Trades</option>
          {trades.filter((t) => t !== 'all').map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {(kindFilter === 'sub' || kindFilter === 'all') && (
          <select
            style={S.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={unified.length === 0 ? 'No vendors yet' : 'No matches'}
          sub={
            unified.length === 0
              ? 'Invite a subcontractor to get started, or add vendors to your directory.'
              : 'Try adjusting search or filters.'
          }
        />
      ) : (
        <div style={S.subList}>
          {filtered.map((r) => {
            const isSub = r.kind === 'sub';
            return (
              <div
                key={`${r.kind}-${r.id}`}
                style={S.subRow}
                onClick={() =>
                  onToast(
                    isSub
                      ? 'View vendor details — Coming in Layer 2.5'
                      : 'View vendor profile — Coming in Layer 2.5'
                  )
                }
              >
                <div style={S.subAvatar}>
                  {(r.displayName || '?').charAt(0).toUpperCase()}
                </div>
                <div style={S.subInfo}>
                  <div style={S.subNameRow}>
                    {isSub && (
                      <span
                        style={{
                          ...S.subStatusDot,
                          background: r.status === 'active' ? '#16A34A' : '#94A3B8',
                        }}
                      />
                    )}
                    <span style={S.subName}>{r.displayName}</span>
                    <span
                      style={{
                        ...S.kindBadge,
                        background: isSub ? '#E0F2FE' : '#F5F3FF',
                        color: isSub ? '#075985' : '#6D28D9',
                      }}
                    >
                      {isSub ? 'Active sub' : 'Directory'}
                    </span>
                    {r.trade && <span style={S.subTradeBadge}>{r.trade}</span>}
                    {r.insuranceVerified && (
                      <span style={S.verifiedInline}>✓ Insured</span>
                    )}
                  </div>
                  <div style={S.subContact}>
                    {r.email && <span>{r.email}</span>}
                    {r.phone && <span>{r.phone}</span>}
                    {r.serviceArea && <span>📍 {r.serviceArea}</span>}
                    {!isSub && r.rating != null && (
                      <span style={S.inlineRating}>
                        ★ {Number(r.rating).toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
                <div style={S.subWoCell}>
                  {isSub ? (
                    r.openWOCount === 0 ? (
                      <span style={S.subWoZero}>0 open</span>
                    ) : (
                      <span
                        style={{
                          ...S.subWoActive,
                          background: r.openWOCount >= 3 ? '#FEF2F2' : '#FFFBEB',
                          color: r.openWOCount >= 3 ? '#DC2626' : '#D97706',
                        }}
                      >
                        {r.openWOCount} open
                      </span>
                    )
                  ) : (
                    <span style={S.subWoZero}>—</span>
                  )}
                </div>
                <div style={S.subStatusCell}>
                  {isSub ? (
                    <span
                      style={{
                        ...S.pill,
                        background: r.status === 'active' ? '#F0FDF4' : '#F1F5F9',
                        color: r.status === 'active' ? '#16A34A' : '#64748B',
                      }}
                    >
                      {r.status === 'active' ? 'Active' : 'Suspended'}
                    </span>
                  ) : (
                    <span
                      style={{
                        ...S.pill,
                        background: '#F1F5F9',
                        color: '#64748B',
                      }}
                    >
                      Pool
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TAB: MESSAGES (vendor/sub/contractor-filtered, AS-PP)
// S6.B (May 19, 2026) — REWRITE: reads flat /messages collection (Path Z)
// instead of nested clients/{cid}/messageThreads. Closes AS-S4-007 per
// AS-S6-005 in S6 matrix. Threads derived client-side via groupBy on
// phone-pair per §6c rules. Maintain-specific filter: only show threads
// likely tied to a vendor/sub (heuristic: msg has workOrderId OR
// participantRole hint in MAINTAIN_MESSAGE_ROLES). Full thread
// experience lives at the cross-cutting Messages tab; this tab is
// the vendor-scoped slice per AS-PP.
// ───────────────────────────────────────────────────────────────────────────

function MessagesView({ messages, loading, error, onToast }) {
  // S6.B AS-S6-005: derive vendor threads from /messages records.
  // Groupby phone-pair, then filter to threads with at least one msg
  // carrying workOrderId (Maintain-side indicator per AS-PP scope).
  // Future enhancement (post-S11): when XC-TRIG writes explicit
  // /messageThreads docs with participantRole, prefer those over
  // heuristic.
  const filtered = useMemo(() => {
    // Group messages into threads (mirror Messages.jsx phonePair logic)
    const groups = {};
    messages.forEach((m) => {
      const pair = [m.toPhone || '', m.fromPhone || ''].filter(Boolean).sort();
      const key = pair.join('|');
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });

    // Build vendor-thread summary rows
    const threads = Object.entries(groups)
      .map(([phonePair, msgs]) => {
        msgs.sort((a, b) => {
          const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
          const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
          return bt - at; // newest first
        });
        const latest = msgs[0];
        const hasWorkOrderTag = msgs.some((m) => !!m.workOrderId);
        const hasRoleHint = msgs.some((m) =>
          MAINTAIN_MESSAGE_ROLES.some((r) =>
            (m.participantRole || '').toLowerCase().includes(r.toLowerCase())
          )
        );
        return {
          id: phonePair,
          phonePair,
          participantName: latest.toPhone || latest.fromPhone || 'Unknown',
          participantRole: hasWorkOrderTag ? 'vendor' : null,
          // S6.C polish: synthesize subject from thread context (workOrderId
          // when present, phone otherwise) so subject and preview carry
          // distinct info. Mirrors Messages.jsx synthesizeSubject() — kept
          // inline here rather than imported because Maintain.jsx is a
          // vendor-scoped slice with simpler context (no AI-04 case).
          subject: (() => {
            const woMsg = msgs.find((m) => !!m.workOrderId);
            if (woMsg) {
              const short = woMsg.workOrderId
                .replace(/^wo-seed-/, '')
                .replace(/^wo-/, '');
              return `WO ${short}`;
            }
            return `Conversation with ${latest.toPhone || latest.fromPhone || 'unknown'}`;
          })(),
          lastMessagePreview: latest.body || '',
          lastMessageAt: latest.createdAt,
          isVendorScoped: hasWorkOrderTag || hasRoleHint,
        };
      })
      .filter((t) => t.isVendorScoped);

    return threads;
  }, [messages]);

  if (loading) return <LoadingState label="Loading vendor messages…" />;
  if (error) {
    return (
      <ErrorState
        label="Could not load messages"
        hint="Check Firestore rules and /messages collection. S6.B retarget completed; if this error persists, audit rules v3.4 deploy."
      />
    );
  }

  return (
    <div style={S.tabBody}>
      <div style={S.sectionHead}>
        <div>
          <div style={S.sectionTitle}>Vendor Messages</div>
          <div style={S.sectionSub}>
            {filtered.length} thread{filtered.length !== 1 ? 's' : ''} with vendors and subs
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={S.btnGhost}
            onClick={() => onToast('AI draft (AI-01) — Use the XC-MSG Messages surface for full compose.')}
          >
            ✨ AI Assist
          </button>
          <button
            style={S.btnPrimary}
            onClick={() => onToast('Open Messages (Cross-cutting nav) to compose a new thread.')}
          >
            + New Thread
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No vendor messages yet"
          sub="Vendor/sub threads (any message carrying a workOrderId) will appear here. Tenant and owner messages live in Operate or the cross-cutting Messages surface."
        />
      ) : (
        <div style={S.threadList}>
          {filtered.map((t) => (
            <div
              key={t.id}
              style={S.threadRow}
              onClick={() => onToast('Open Messages (Cross-cutting nav) for full thread detail.')}
            >
              <div style={S.threadRowLeft}>
                <div style={S.threadParticipant}>
                  {t.participantName || 'Unknown'}
                  {t.participantRole && (
                    <span style={S.threadRole}>{t.participantRole}</span>
                  )}
                </div>
                <div style={S.threadSubject}>{t.subject || '(no subject)'}</div>
                <div style={S.threadPreview}>{t.lastMessagePreview || ''}</div>
              </div>
              <div style={S.threadDate}>{fmtDate(t.lastMessageAt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TAB: ASSETS (AS-QQ duplication — Create-WO emphasis)
// ───────────────────────────────────────────────────────────────────────────

function AssetsView({ assets, loading, error, onToast }) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = useMemo(() => {
    const uniq = new Set(assets.map((a) => a.category).filter(Boolean));
    return ['all', ...Array.from(uniq).sort()];
  }, [assets]);

  const filtered = useMemo(() => {
    return assets.filter((a) => {
      if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (a.name || '').toLowerCase().includes(s) ||
          (a.manufacturer || '').toLowerCase().includes(s) ||
          (a.model || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [assets, search, categoryFilter]);

  if (loading) return <LoadingState label="Loading assets…" />;
  if (error) {
    return (
      <ErrorState
        label="Could not load assets"
        hint="assets collection seeds with S11. Empty state expected pre-seed."
      />
    );
  }

  return (
    <div style={S.tabBody}>
      <div style={S.sectionHead}>
        <div>
          <div style={S.sectionTitle}>Assets — Maintenance View</div>
          <div style={S.sectionSub}>
            {assets.length} tracked asset{assets.length !== 1 ? 's' : ''} · click to create work order
          </div>
        </div>
        <button
          style={S.btnGhost}
          onClick={() => onToast('AI condition + lifecycle prediction — Coming in Layer 2.5')}
        >
          ✨ AI Assist
        </button>
      </div>

      <div style={S.toolbar}>
        <input
          style={{ ...S.search, flex: 1 }}
          placeholder="Search assets…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={S.select}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c === 'all' ? 'All categories' : c}
            </option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={assets.length === 0 ? 'No assets tracked yet' : 'No matches'}
          sub={
            assets.length === 0
              ? 'Asset register seeds with S11. Once seeded, every component shows here with WO-creation shortcut.'
              : 'Try adjusting search or filter.'
          }
        />
      ) : (
        <div style={S.assetGrid}>
          {filtered.map((a) => {
            const cond = getAssetCondition(a.conditionScore);
            const installDate = parseAssetDate(a.installDate);
            const expectedLife = Number(a.expectedLifeYears) || 0;
            let pctConsumed = null;
            if (installDate && expectedLife > 0) {
              const ageYears = (Date.now() - installDate.getTime()) / (365.25 * 24 * 3600 * 1000);
              pctConsumed = Math.min(100, Math.max(0, (ageYears / expectedLife) * 100));
            }
            const lifeBarColor =
              pctConsumed == null ? '#CBD5E1'
                : pctConsumed >= 90 ? '#DC2626'
                : pctConsumed >= 70 ? '#D97706'
                : '#16A34A';

            return (
              <div key={a.id} style={S.assetCard}>
                <div style={S.assetCardHead}>
                  <div style={S.assetName}>{a.name || '(unnamed asset)'}</div>
                  {cond && (
                    <span style={{ ...S.conditionBadge, color: cond.color, background: cond.bg }}>
                      {cond.label}
                    </span>
                  )}
                </div>
                {a.category && <div style={S.assetCategory}>{a.category}</div>}
                <div style={S.assetMeta}>
                  {a.manufacturer && <div>{a.manufacturer}{a.model ? ` · ${a.model}` : ''}</div>}
                  {a.serialNumber && <div>SN: {a.serialNumber}</div>}
                  {installDate && <div>Installed {fmtDateLong(installDate)}</div>}
                </div>
                {pctConsumed != null && (
                  <div style={S.lifecycleWrap}>
                    <div style={S.lifecycleLabel}>
                      <span>Lifecycle</span>
                      <span>{Math.round(pctConsumed)}%</span>
                    </div>
                    <div style={S.lifecycleBar}>
                      <div
                        style={{
                          ...S.lifecycleFill,
                          width: `${pctConsumed}%`,
                          background: lifeBarColor,
                        }}
                      />
                    </div>
                  </div>
                )}
                <div style={S.assetActions}>
                  <button
                    style={S.btnPrimarySm}
                    onClick={() =>
                      onToast(`Create WO against ${a.name || 'asset'} — Coming in Layer 2.5`)
                    }
                  >
                    + Create WO
                  </button>
                  <button
                    style={S.btnGhostSm}
                    onClick={() => onToast('Service history — Coming in Layer 2.5')}
                  >
                    History
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ───────────────────────────────────────────────────────────────────────────

export default function Maintain({ workspace, roles, roleLabel }) {
  // Match Operate.jsx prop signature (S34 pattern). The workspace object
  // carries workspaceId, which IS the clientId in live state per memory
  // ("each LLC = separate Workspace = separate client doc"). Some live
  // workspace docs may also expose clientId directly — accept both.
  const activeClientId = workspace?.workspaceId || workspace?.clientId || null;

  const [activeTab, setActiveTab] = useState('overview');
  const [toastMsg, setToastMsg] = useState('');

  const onToast = useCallback((msg) => {
    setToastMsg(msg);
  }, []);

  // ── Read paths ──────────────────────────────────────────────────────────
  // AS-RR: workOrders nested under clients/{cid}/
  const wosResult = useNestedClientCollection(activeClientId, 'workOrders', {
    orderField: 'createdAt',
    orderDir: 'desc',
  });

  // Network = clients/{cid}/vendors (directory pool)
  const vendorsResult = useNestedClientCollection(activeClientId, 'vendors', {
    orderField: 'name',
    orderDir: 'asc',
  });

  // Vendors tab = clientMembers with role=Subcontractor (active subs)
  // Memoize the extraFilter object to keep stable reference
  const subsFilter = useMemo(
    () => ({ field: 'role', op: '==', value: 'Subcontractor' }),
    []
  );
  const subsResult = useFilteredCollection(
    'clientMembers',
    'clientId',
    activeClientId,
    subsFilter
  );

  // ── S6.B RETARGET (May 19, 2026): /messages flat top-level ──────────────
  // OLD: useNestedClientCollection(activeClientId, 'messageThreads')
  //   → read nested clients/{cid}/messageThreads
  //   → that path was deleted in S2 along with whole /clients tree
  //   → v3.3 rules did not cover the nested path
  //   → result: persistent permission-denied error in console (AS-S4-007)
  //
  // NEW: useFilteredCollection('messages', 'clientId', activeClientId)
  //   → reads flat top-level /messages collection (Path Z, AS-S6-001)
  //   → matches what deployed doorstack_twilio.js sendSms writes to
  //   → firestore.rules v3.4 added /messages block in S6.A
  //   → threads derived client-side in MessagesView per §6c rules
  //   → closes AS-S4-007 per AS-S6-005
  const messagesResult = useFilteredCollection(
    'messages',
    'clientId',
    activeClientId
  );

  // Assets tab — same source as Operate-side (top-level workspaceId-filtered).
  // Reuses S35 pattern; AS-LL empty state expected pre-S11 seed.
  const assetsResult = useFilteredCollection(
    'assets',
    'workspaceId',
    activeClientId
  );

  // ── Render ──────────────────────────────────────────────────────────────
  if (!activeClientId) {
    return (
      <div style={S.container}>
        <ErrorState
          label="Workspace not selected"
          hint="Select a workspace from the top bar dropdown."
        />
      </div>
    );
  }

  let content = null;
  switch (activeTab) {
    case 'overview':
      content = (
        <OverviewView
          wos={wosResult.data}
          vendors={vendorsResult.data}
          subs={subsResult.data}
          assets={assetsResult.data}
          messages={messagesResult.data}
          onToast={onToast}
        />
      );
      break;
    case 'wos':
      content = (
        <WorkOrdersView
          wos={wosResult.data}
          loading={wosResult.loading}
          error={wosResult.error}
          onToast={onToast}
        />
      );
      break;
    case 'board':
      content = (
        <BoardView
          wos={wosResult.data}
          loading={wosResult.loading}
          error={wosResult.error}
          onToast={onToast}
        />
      );
      break;
    case 'vendors':
      content = (
        <VendorsView
          subs={subsResult.data}
          vendors={vendorsResult.data}
          wos={wosResult.data}
          subsLoading={subsResult.loading}
          vendorsLoading={vendorsResult.loading}
          subsError={subsResult.error}
          vendorsError={vendorsResult.error}
          onToast={onToast}
        />
      );
      break;
    case 'messages':
      // S6.B: pass /messages records (not /messageThreads docs).
      // MessagesView does the vendor-thread derivation client-side.
      content = (
        <MessagesView
          messages={messagesResult.data}
          loading={messagesResult.loading}
          error={messagesResult.error}
          onToast={onToast}
        />
      );
      break;
    case 'assets':
      content = (
        <AssetsView
          assets={assetsResult.data}
          loading={assetsResult.loading}
          error={assetsResult.error}
          onToast={onToast}
        />
      );
      break;
    default:
      content = <EmptyState title="Unknown tab" />;
  }

  return (
    <div style={S.container}>
      <div style={S.tabStrip}>
        {MAINTAIN_TABS.map((t) => (
          <button
            key={t.key}
            style={{
              ...S.tab,
              ...(activeTab === t.key ? S.tabActive : {}),
            }}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {content}

      <Toast message={toastMsg} onDismiss={() => setToastMsg('')} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// STYLES — inline hex literals only (AS-X)
// ───────────────────────────────────────────────────────────────────────────

const S = {
  container: {
    background: '#F8FAFC',
    minHeight: '100%',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#0F172A',
  },

  // Top tab strip — mobile-scrollable (S17 P3b pattern)
  tabStrip: {
    display: 'flex',
    gap: 4,
    padding: '12px 16px 0',
    background: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
  },
  tab: {
    flex: '0 0 auto',
    padding: '10px 16px',
    border: 'none',
    background: 'transparent',
    color: '#64748B',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  tabActive: {
    color: '#0891B2',
    borderBottom: '2px solid #0891B2',
  },

  // Tab body
  tabBody: { padding: '20px 24px' },

  // Section header
  sectionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#0F172A',
    margin: 0,
  },
  sectionSub: {
    fontSize: 13,
    color: '#64748B',
    marginTop: 4,
  },

  // Buttons
  btnPrimary: {
    background: '#0891B2',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhost: {
    background: '#FFFFFF',
    color: '#0891B2',
    border: '1px solid #0891B2',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnPrimarySm: {
    background: '#0891B2',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhostSm: {
    background: '#FFFFFF',
    color: '#475569',
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Toolbar
  toolbar: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  search: {
    padding: '8px 12px',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    fontSize: 13,
    background: '#FFFFFF',
    outline: 'none',
    minWidth: 220,
    fontFamily: 'inherit',
    color: '#0F172A',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    fontSize: 13,
    background: '#FFFFFF',
    outline: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: '#0F172A',
  },

  // Filter tabs (within Work Orders)
  filterTabs: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    flex: 1,
  },
  filterTab: {
    padding: '6px 12px',
    borderRadius: 16,
    border: '1px solid #CBD5E1',
    background: '#FFFFFF',
    color: '#475569',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontFamily: 'inherit',
  },
  filterTabActive: {
    background: '#0891B2',
    color: '#FFFFFF',
    borderColor: '#0891B2',
  },
  filterTabCount: {
    background: '#E2E8F0',
    color: '#475569',
    borderRadius: 10,
    padding: '1px 6px',
    fontSize: 11,
    fontWeight: 700,
  },
  filterTabCountActive: {
    background: 'rgba(255,255,255,0.25)',
    color: '#FFFFFF',
  },

  // Table
  tableWrap: {
    background: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #E2E8F0',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #E2E8F0',
    background: '#F8FAFC',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    cursor: 'pointer',
    borderBottom: '1px solid #F1F5F9',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    color: '#0F172A',
    verticalAlign: 'middle',
  },
  tdDesc: {
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  woId: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#0891B2',
    fontWeight: 700,
  },
  unassigned: { color: '#94A3B8', fontStyle: 'italic' },

  // Pills
  pill: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
  },
  urgencyBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    display: 'inline-block',
  },

  // Empty / loading / error
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid #E2E8F0',
  },
  emptyTitle: { fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 6 },
  emptySub: { fontSize: 13, color: '#64748B' },
  loading: {
    padding: 60,
    textAlign: 'center',
    color: '#64748B',
    fontSize: 13,
  },
  error: {
    padding: 40,
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 10,
    textAlign: 'center',
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#991B1B',
    marginBottom: 6,
  },
  errorHint: { fontSize: 12, color: '#7F1D1D' },

  // KPI grid (Overview)
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  kpiTile: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: '14px 16px',
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#0F172A', marginBottom: 2 },
  kpiSub: { fontSize: 11, color: '#94A3B8' },

  // Overview split
  overviewSplit: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 12,
  },
  overviewCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: 16,
  },
  overviewCardHead: {
    fontSize: 13,
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #F1F5F9',
  },
  overviewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #F8FAFC',
    gap: 12,
  },
  overviewRowLeft: { flex: 1, minWidth: 0 },
  overviewRowTitle: { fontSize: 13, fontWeight: 600, color: '#0F172A' },
  overviewRowSub: { fontSize: 11, color: '#64748B', marginTop: 2 },
  overviewRowCount: { fontSize: 18, fontWeight: 700, color: '#0891B2' },
  overviewEmpty: {
    fontSize: 13,
    color: '#94A3B8',
    fontStyle: 'italic',
    padding: '12px 0',
  },

  // Board
  board: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 8,
    alignItems: 'flex-start',
  },
  boardColumn: {
    flex: '0 0 240px',
    minWidth: 220,
    display: 'flex',
    flexDirection: 'column',
  },
  boardColHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: '#FFFFFF',
    borderRadius: '10px 10px 0 0',
    border: '1px solid #E2E8F0',
    borderBottom: 'none',
  },
  boardColTitle: { fontSize: 13, fontWeight: 700 },
  boardColCount: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
  },
  boardColBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 8,
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '0 0 10px 10px',
    minHeight: 80,
    maxHeight: 'calc(100vh - 320px)',
    overflowY: 'auto',
  },
  boardEmpty: {
    fontSize: 12,
    color: '#94A3B8',
    textAlign: 'center',
    padding: '20px 8px',
    fontStyle: 'italic',
  },
  boardCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  boardCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  boardCardId: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748B',
    fontFamily: 'monospace',
    letterSpacing: '0.5px',
  },
  boardCardDesc: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0F172A',
    marginBottom: 8,
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  boardCardMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    marginBottom: 8,
  },
  boardCardMetaRow: { fontSize: 11, color: '#64748B' },
  boardCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  boardCardDate: { fontSize: 11, color: '#94A3B8' },
  boardCardAmount: { fontSize: 12, fontWeight: 700, color: '#0F172A' },

  // Vendor grid (Network tab)
  vendorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 14,
  },
  vendorCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: 16,
    cursor: 'pointer',
  },
  vendorName: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 3,
  },
  vendorCategory: {
    fontSize: 11,
    color: '#0891B2',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 10,
  },
  vendorMeta: {
    fontSize: 12,
    color: '#475569',
    marginBottom: 8,
  },
  vendorMetaRow: { padding: '2px 0' },
  vendorRating: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#F59E0B',
    fontSize: 13,
    marginBottom: 8,
  },
  vendorRatingNum: { color: '#475569', fontWeight: 600, fontSize: 12 },
  verifiedBadge: {
    display: 'inline-block',
    background: '#DCFCE7',
    color: '#15803D',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 12,
  },

  // Sub list (Vendors tab)
  subList: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    overflow: 'hidden',
  },
  subRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderBottom: '1px solid #F1F5F9',
    cursor: 'pointer',
  },
  subAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#E0F2FE',
    color: '#0891B2',
    fontSize: 14,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  subInfo: { flex: 1, minWidth: 0 },
  subNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  subStatusDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  subName: { fontSize: 14, fontWeight: 600, color: '#0F172A' },
  subTradeBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 8,
    background: '#F1F5F9',
    color: '#0F172A',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  subContact: {
    fontSize: 12,
    color: '#64748B',
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  subWoCell: { width: 90, textAlign: 'center', flexShrink: 0 },
  subWoZero: {
    fontSize: 11,
    fontWeight: 600,
    color: '#64748B',
    padding: '2px 8px',
    borderRadius: 10,
    background: '#F1F5F9',
  },
  subWoActive: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
  },
  subStatusCell: { width: 90, textAlign: 'center', flexShrink: 0 },

  // Combined Vendors tab — kind filter segmented control
  kindFilterRow: {
    display: 'flex',
    gap: 0,
    marginBottom: 12,
    background: '#F1F5F9',
    borderRadius: 8,
    padding: 3,
    width: 'fit-content',
    maxWidth: '100%',
    overflowX: 'auto',
    flexWrap: 'nowrap',
  },
  kindFilterBtn: {
    padding: '6px 14px',
    border: 'none',
    background: 'transparent',
    color: '#64748B',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 6,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  kindFilterBtnActive: {
    background: '#FFFFFF',
    color: '#0F172A',
    boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
  },

  // Combined Vendors tab — kind badge on each row
  kindBadge: {
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  // Combined Vendors tab — soft warning banner (one source failed)
  softWarn: {
    background: '#FFFBEB',
    border: '1px solid #FCD34D',
    color: '#92400E',
    fontSize: 12,
    padding: '8px 12px',
    borderRadius: 6,
    marginBottom: 12,
  },

  // Combined Vendors tab — inline rating + verified for directory entries
  inlineRating: {
    color: '#F59E0B',
    fontWeight: 600,
  },
  verifiedInline: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 8,
    background: '#DCFCE7',
    color: '#15803D',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },

  // Messages
  threadList: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    overflow: 'hidden',
  },
  threadRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '14px 16px',
    borderBottom: '1px solid #F1F5F9',
    cursor: 'pointer',
    gap: 12,
  },
  threadRowLeft: { flex: 1, minWidth: 0 },
  threadParticipant: {
    fontSize: 13,
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  threadRole: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 8,
    background: '#F1F5F9',
    color: '#475569',
    textTransform: 'uppercase',
  },
  threadSubject: {
    fontSize: 13,
    color: '#0F172A',
    fontWeight: 500,
    marginBottom: 2,
  },
  threadPreview: {
    fontSize: 12,
    color: '#64748B',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  threadDate: { fontSize: 11, color: '#94A3B8', flexShrink: 0 },

  // Asset cards
  assetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 14,
  },
  assetCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: 16,
  },
  assetCardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 4,
  },
  assetName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0F172A',
    flex: 1,
    minWidth: 0,
  },
  conditionBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    flexShrink: 0,
  },
  assetCategory: {
    fontSize: 11,
    color: '#0891B2',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 10,
  },
  assetMeta: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 1.6,
    marginBottom: 10,
  },
  lifecycleWrap: { marginBottom: 12 },
  lifecycleLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 11,
    fontWeight: 600,
    color: '#64748B',
    marginBottom: 4,
  },
  lifecycleBar: {
    height: 6,
    background: '#F1F5F9',
    borderRadius: 3,
    overflow: 'hidden',
  },
  lifecycleFill: {
    height: '100%',
    transition: 'width 0.3s',
  },
  assetActions: {
    display: 'flex',
    gap: 8,
    paddingTop: 4,
  },

  // Toast
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#0F172A',
    color: '#FFFFFF',
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 500,
    boxShadow: '0 10px 30px rgba(15,23,42,0.18)',
    zIndex: 1000,
    maxWidth: '90%',
    textAlign: 'center',
  },
};
